import BaseService from './BaseService';
import {dataTypes, featureTypeToDataType} from "./Constants";
import Request from "./Request";
import UserData from "../data/UserData";
import type { UserRawData } from "../data/UserData";
import Config from "../common/Config";
import NetworkService from "./NetworkService";
import {LocaleService} from "./LocaleService";
import AppService from './AppService';
import type { LngLatBounds } from 'mapbox-gl';

/**
 * Respuesta cruda del REST.
 *
 * El backend puede responder con un campo `error` en vez de con los datos, asi
 * que cada consumidor estrecha lo que necesita en lugar de asumir la forma.
 */
export interface RawResponse {
    error?: unknown;
    [key: string]: unknown;
}

/**
 * Respuesta de /updates/gronze_maps.
 */
export interface AppUpdatesData extends RawResponse {
    app_version: string;
}

/**
 * Handles backend connections through Request service and
 * returns promise-wrapped raw data from server.
 *
 * @todo Remove await and return Promises.
 */
class RestService extends BaseService {
    static hotelPrefix = 'hotel';
    static locationPrefix = 'place';
    static hotelPath = '/hotel/';
    static placePath = '/city/';
    static stagePath = '/stage/';
    static trackPath = '/track/';
    static pagePath = '/page/';
    static poiPath = '/poi/';
    static cityListPath = '/data/city_list.json';

    // Path prefix for all the API
    static pathPrefix = '/maps20';
    // service error status
    static errorLoaded = false;

    /**
     * Gets rest URL for object type.
     *
     * @param type Location type (hotel, place);
     */
    static getUrlForDataType(type: string, id: string | number): string {
      let path;
      switch (type) {
        case dataTypes.hotel:
          path = this.hotelPath + id;
          break;
        case dataTypes.place:
          path = this.placePath + id;
          break;
        case dataTypes.stage:
          path = this.stagePath + id;
          break;
        case dataTypes.track:
          path = this.trackPath + id;
          break;
        case dataTypes.page:
          path = this.pagePath + id;
          break;
        case dataTypes.poi:
          path = this.poiPath + id;
          break;
        default:
          // No action.
      }

      return path ? Config.getRestUrl() + this.pathPrefix + path : '';
    }

    /**
     * Gets rest URL for object type.
     *
     * @param type Location type (hotel, place);
     */
    static getUrlForFeatureType(type: string, id: string | number): string {
      return this.getUrlForDataType(featureTypeToDataType(type), id);
    }

    /**
     * Request data, handle errors.
     *
     * @param url
     *   The request path with '.json' suffix.
     * @param query_string
     *   Optional query string to append to the path.
     * @param fullUrl
     *   True if the first parameter is a full Url
     * @param withAuth
     *   True if we need to use authentication
     * @param signal
     *   Para abortar la petición.
     *
     * @return Parsed response body if successful, empty otherwise.
     */
    static async getRawData<T extends RawResponse = RawResponse>(
        url: string,
        query_string: string | null = null,
        fullUrl: boolean | null = false,
        withAuth = true,
        signal?: AbortSignal,
    ): Promise<T | [] | undefined> {
        if (NetworkService.isOffline()) {
            return;
        }

        let urlData = url;

        if (!fullUrl) {
          urlData = this.pathPrefix + urlData;
        }

        if (query_string) {
          urlData = urlData + '?' + query_string;
        }

        try {
          const res = await Request.get<T>(urlData, withAuth, signal);
          // stores error status to reload data cases
          this.errorLoaded = !res || !!res.error;
          if (!this.errorLoaded) {
              return res;
          } else {
            this.logger().warn("RestService: Error response", res.error);
            return [];
          }
        }
        catch (error) {
          this.logger().error("RestService: Error: ", error);
          this.errorLoaded = true;
        }

    }

     /**
      * Load from services by object type, id.
      *
      * @param type Data type
      *
      * @return Returned raw data if successful
      */
     static async loadFromTypeId(type: string, id: string | number) {
       const url = this.getUrlForDataType(type, id);
       return this.getRawData(url, null, true);
     }

    /**
     * Gets the full city list.
     *
     * @return Array of raw city objects {id, name, latlon, size, priority}.
     */
    static async getCityList() {
        return await this.getRawData(this.cityListPath);
    }

    /*
     * Get accommodations for given bounds.
     *
     * @param bounds
     *   Map bounds, https://docs.mapbox.com/mapbox-gl-js/api/geography/#lnglatbounds
     *
     * @return Parsed response body if any, none if empty
     */
    static async getHotels(bounds: LngLatBounds) {
        const geoLatMin = bounds._sw.lat;
        const geoLatMax = bounds._ne.lat;
        const geoLonMin = bounds._sw.lng;
        const geoLonMax = bounds._ne.lng;
        const urlPath = '/hotel_list.json';
        let urlData = '';
        urlData += 'geo_lat[min]=' + geoLatMin;
        urlData += '&geo_lat[max]=' + geoLatMax;
        urlData += '&geo_lon[min]=' + geoLonMin;
        urlData += '&geo_lon[max]=' + geoLonMax;
        if (LocaleService.getLanguage() !== 'es') {
            urlData += '&lang=' + LocaleService.getLanguage();
        }

        return this.getRawData(urlPath, urlData);
    }

     /*
     * Get route list
     *
     * @return Parsed response body if any, none if empty
     */
    static async getRouteList() {
        const urlPath = '/route_list.json';
        const urlData = '';

        return this.getRawData(urlPath, urlData);
    }

    /**
     * Gets current user data.
     *
     * @return User data if successful.
     */
    static async getCurrentUser(signal?: AbortSignal): Promise<UserData> {
      const deviceInfo = AppService.getDeviceInfoQueryString();
      const user_data = await this.getRawData('/status/current_user', deviceInfo, false, true, signal);
      if (user_data) {
        this.logger().debug("Received user data", user_data);
        return UserData.fromRawData((user_data as RawResponse).user as UserRawData);
      } else {
        return Promise.reject("Cannot load user data.");
      }
    }

    /**
     * Gets App status from server.
     *
     * @return Object with 'app_active', 'app_version', 'app_config'
     */
    static async getAppStatus() {
      const app_data = await this.getRawData('/status/gronze_maps');
      if (app_data) {
        this.logger().debug("Received app status data", app_data);
        return app_data;
      }
    }

     /**
     * Gets App updates from server w/o authentication.
     *
     * @return Object with 'app_active' and 'app_version'
     */
    static async getAppUpdates(): Promise<AppUpdatesData | undefined> {
      const deviceInfo = AppService.getDeviceInfoQueryString();
      const app_data = await this.getRawData<AppUpdatesData>('/updates/gronze_maps', deviceInfo, null, false);
      if (app_data) {
        this.logger().debug("Received app updates data", app_data);
        return app_data as AppUpdatesData;
      }
    }

    /*
     * Get accommodations for given bounds.
     *
     * @param bounds
     *   Map bounds, https://docs.mapbox.com/mapbox-gl-js/api/geography/#lnglatbounds
     *
     * @return Parsed response body if any, none if empty
     */
    static async getPOIs(bounds: LngLatBounds) {
        const geoLatMin = bounds._sw.lat;
        const geoLatMax = bounds._ne.lat;
        const geoLonMin = bounds._sw.lng;
        const geoLonMax = bounds._ne.lng;
        const urlPath = '/poi_list.json';
        let urlData = '';
        urlData += 'geo_lat[min]=' + geoLatMin;
        urlData += '&geo_lat[max]=' + geoLatMax;
        urlData += '&geo_lon[min]=' + geoLonMin;
        urlData += '&geo_lon[max]=' + geoLonMax;
        if (LocaleService.getLanguage() !== 'es') {
            urlData += '&lang=' + LocaleService.getLanguage();
        }

        const response = await this.getRawData(urlPath, urlData);
        return  response ? response : [];
    }
}

export default RestService;
